import * as A from 'astronomy-engine';
import type { DeepSky } from '../data/catalog';
import { eyeLimitingMagnitude, eyeSkyBrightness } from './skybrightness';

// "What is worth finding tonight?" For the Messier objects and the brightest named showpieces, work
// out when each is best placed in the dark hours and how clearly it will show with eyes, binoculars or
// a small telescope from this sky, allowing for the Moon, light pollution and height above the horizon.
// A guide, not a guarantee: the numbers are standard rules of thumb (below).

export type Instrument = 'eyes' | 'binoculars' | 'telescope';

/** How much fainter each can reach than the naked eye (7×50 binoculars; a 100–150 mm telescope). */
const GAIN: Record<Instrument, number> = { eyes: 0, binoculars: 3.5, telescope: 5.5 };

export interface Sight {
  index: number; // into the deep-sky list
  t: number; // best time (ms)
  alt: number;
  az: number;
  margin: number; // magnitudes to spare at the best time
  level: 'easy' | 'fair' | 'hard';
}

/** Types worth recommending (stars and novae are left to the star lists). */
const TYPES = /cluster|galaxy|nebula|remnant|association/i;

/**
 * The surface brightness (mag/arcsec²) of the part of an extended object that shows first, or null for
 * clusters (their stars show whatever the background) and unknown sizes. Catalogue sizes take in faint
 * outskirts, so for large galaxies and globulars the bright middle is used: about a fifth of the light in
 * a tenth of the width, 3.25 mag/arcsec² brighter than the average. Nebulae and small objects get a
 * smaller allowance; planetary nebulae and supernova remnants are fairly even and keep their average.
 */
function surfaceBrightness(d: DeepSky): number | null {
  if (!d.maj || d.mag == null || /open cluster|association|cluster with nebula/i.test(d.t)) return null;
  const min = d.min ?? d.maj * 0.7;
  const area = (Math.PI / 4) * d.maj * min * 3600; // arcsec²
  const sb = d.mag + 2.5 * Math.log10(area);
  if (/planetary|remnant/i.test(d.t)) return sb;
  if (d.maj < 10 || /nebula/i.test(d.t)) return sb - 1.5; // big nebulae glow fairly evenly (North America)
  // Diffuse spirals (low average brightness, like M33 or M101) have weak middles.
  return sb - (/galaxy/i.test(d.t) && sb > 22.5 ? 2 : 3.25);
}

export function bestSights(
  dsos: DeepSky[],
  obs: A.Observer,
  from: number,
  to: number,
  instrument: Instrument,
  bortle: number,
  sqm: number | null,
): Sight[] {
  // Dark-hour samples every half hour, with the Sun and Moon at each.
  const steps: { t: number; d: Date; sunAlt: number; moonAlt: number; moonMag: number }[] = [];
  // On the hour and half hour, so the suggested times read naturally.
  for (let t = Math.ceil(from / 1800e3) * 1800e3; t <= to; t += 30 * 60e3) {
    const d = new Date(t);
    const alt = (b: A.Body) => {
      const e = A.Equator(b, d, obs, true, true);
      return A.Horizon(d, obs, e.ra, e.dec, 'normal').altitude;
    };
    const sunAlt = alt(A.Body.Sun);
    if (sunAlt > -10) continue;
    steps.push({ t, d, sunAlt, moonAlt: alt(A.Body.Moon), moonMag: A.Illumination(A.Body.Moon, d).mag });
  }
  if (!steps.length) return [];
  // The eye's limit and the sky overhead, as Naked eye has them (render/nakedeye.ts).
  const sky = steps.map((s) => ({
    nelm: eyeLimitingMagnitude(s.sunAlt, s.moonAlt, s.moonMag, bortle, sqm),
    S: eyeSkyBrightness(s.sunAlt, s.moonAlt, s.moonMag, bortle, sqm),
  }));
  const out: Sight[] = [];
  dsos.forEach((d, index) => {
    if (d.mag == null || !TYPES.test(d.t)) return;
    if (!d.m && !((d.name || d.known) && d.mag <= 7)) return; // Messier, or a bright well-known showpiece
    const sb = surfaceBrightness(d);
    let best: Sight | null = null;
    steps.forEach((s, k) => {
      const h = A.Horizon(s.d, obs, d.ra / 15, d.dec, 'normal');
      if (h.altitude < 20) return;
      // Air mass dims things low down (0.25 mag per extra air mass, a typical clear night).
      const airmass = 1 / Math.sin((h.altitude + 244 / (165 + 47 * h.altitude ** 1.1)) * (Math.PI / 180));
      const dim = 0.25 * (airmass - 1);
      const reach = sky[k].nelm + GAIN[instrument] - 0.5; // compact things, with a little in hand
      let margin = reach - (d.mag! + dim);
      // Big faint things need contrast against the sky: roughly visible if no more than 1.5 mag/arcsec²
      // fainter than the background (magnification darkens both alike, so the instrument does not help).
      if (sb != null) margin = Math.min(margin, sky[k].S + 1.5 - (sb + dim) + (instrument === 'eyes' ? 0 : 0.5));
      if (!best || margin > best.margin) best = { index, t: s.t, alt: h.altitude, az: h.azimuth, margin, level: 'hard' };
    });
    if (best && (best as Sight).margin > 0) {
      const b = best as Sight;
      b.level = b.margin > 2 ? 'easy' : b.margin > 0.8 ? 'fair' : 'hard';
      out.push(b);
    }
  });
  // One entry per patch of sky: a Messier object wins over a neighbouring showpiece (the Orion Nebula
  // over the Sword clusters), and of two close neighbours the easier stays (h and χ Persei).
  const sep = (a: DeepSky, b: DeepSky) => {
    const r = Math.PI / 180;
    const c = Math.sin(a.dec * r) * Math.sin(b.dec * r) + Math.cos(a.dec * r) * Math.cos(b.dec * r) * Math.cos((a.ra - b.ra) * r);
    return Math.acos(Math.min(1, c)) / r;
  };
  const ranked = out.sort((a, b) => Number(!!dsos[b.index].m) - Number(!!dsos[a.index].m) || b.margin - a.margin);
  const kept: Sight[] = [];
  for (const x of ranked) if (!kept.some((k) => sep(dsos[k.index], dsos[x.index]) < 1.2)) kept.push(x);
  return kept.sort((a, b) => b.margin - a.margin);
}
